/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext } from '@msdyn365-commerce/core';
import { readAsync, updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { Address, Customer } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { createAddressManagementInput, IAddressManagementInput } from './add-address';
import { GetCustomerInput } from './get-customer';

export function updatePrimaryAddressHandler(customer: Customer, address: Address): Customer {
    customer.Addresses = setPrimaryAddress({ ...address }, [...(customer.Addresses || [])]);
    return customer;
}

export async function updatePrimaryAddressAction(input: IAddressManagementInput, ctx: IActionContext): Promise<Address[]> {
    const { address } = input;

    const customer = await readAsync({ callerContext: ctx, bypassCache: 'get' }, '');

    if (!customer) {
        throw new Error('Unable to find customer');
    }

    customer.Addresses = setPrimaryAddress({ ...address }, [...(customer.Addresses ?? [])]);
    const updatedCustomer = await updateAsync({ callerContext: ctx }, customer);

    if (!updatedCustomer) {
        throw new Error('Unable to update customer');
    }

    ctx.update(new GetCustomerInput(ctx.requestContext.apiSettings), updatedCustomer);

    return updatedCustomer.Addresses || [];
}

/**
 * The updatePrimaryAddress data action
 * Calls the read RetailServer API to get information about a customer
 * Merge the passed address information with the address information from
 * the retireved customer, and then updates that customer via the update RetailServer API.
 */
export const updatePrimaryAddressActionDataAction = createObservableDataAction<Address[]>({
    id: '@msdyn365-commerce-modules/retail-actions/update-primary-address',
    action: <IAction<Address[]>>updatePrimaryAddressAction,
    input: <(args: ICreateActionContext) => IActionInput>(<IAny>createAddressManagementInput),
    isBatched: false
});
export default updatePrimaryAddressActionDataAction;

const setPrimaryAddress = (primaryAddresses: Address, addresses: Address[]): Address[] => {
    return addresses.map(addr => {
        if (addr.RecordId === primaryAddresses.RecordId) {
            addr.IsPrimary = true;
        } else {
            addr.IsPrimary = false;
        }
        return addr;
    });
};
